<template>
  <canvas ref="canvas" class="h-full w-full" />
</template>

<script setup lang="ts">
import { onMounted, shallowRef, useTemplateRef } from 'vue'

import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import {
  drawImageToCanvas,
  drawMagnifyingGlass,
  loadImagesWithInterval,
  useCanvasPosition,
} from './ImagesSequence.utils'

const props = defineProps<{
  activeSequence: number
  format: string
  imagesCount: number
  sequencesCount: number
}>()

export type ImagesSequence = {
  images: Array<HTMLImageElement>
  alreadyLoaded: Set<number>
}

const logger = useLogger()

const imagesSequences: Array<ImagesSequence> = Array.from(
  { length: props.sequencesCount },
  () => ({
    images: [],
    alreadyLoaded: new Set<number>(),
  }),
)

const canvas = useTemplateRef<HTMLCanvasElement>('canvas')
const ctx = shallowRef<CanvasRenderingContext2D | null>(null)

const canvasSizes: { width: number; height: number } = {
  width: 0,
  height: 0,
}

let devicePixelR = 1

const { canvasPos, updateCanvasPosition } = useCanvasPosition(canvas)

/**
 *
 * Functions
 */

function updateSequence(
  progress: number,
  activeSequenceIndex: number,
  alpha: number,
) {
  if (!ctx.value) return

  const activeImages = imagesSequences[activeSequenceIndex].images

  const imagesProgress = Math.floor(progress * (activeImages.length - 1))

  if (activeImages[imagesProgress] !== undefined) {
    drawImageToCanvas({
      alpha,
      image: activeImages[imagesProgress],
      canvasSizes,
      devicePixelR,
      ctx: ctx.value,
    })
  }
}

/**
 *
 * @param xPos - cursor x position
 * @param yPos - cursor y position
 */

function addZoomEffect(params: {
  activeSequenceIndex: number
  progress: number
  xPos: number
  yPos: number
  zoomIntensity: number
  zoomSize: number
}) {
  if (!ctx.value) return

  const { activeSequenceIndex, progress, xPos, yPos, zoomIntensity, zoomSize } =
    params

  const activeImages = imagesSequences[activeSequenceIndex].images
  const imagesProgress = Math.floor(progress * (activeImages.length - 1))

  // x is the x-position of the glass center relative to the canvas (top-right is (0,0))
  const x = xPos - canvasPos.x
  // y is the y-position of the glass center relative to the canvas (top-right is (0,0))
  const y = yPos - canvasPos.y

  drawMagnifyingGlass({
    canvasSizes,
    devicePixelR,
    ctx: ctx.value,
    image: activeImages[imagesProgress],
    xPos: x,
    yPos: y,
    zoomIntensity,
    zoomSize,
  })
}

/**
 *
 * Loading functions
 */

async function loadImagesAtInterval(
  path: string,
  sequenceIndex: number,
  callback?: () => void,
  interval = 1,
) {
  const activeSequence = imagesSequences[sequenceIndex]

  try {
    const { images, alreadyLoaded } = await loadImagesWithInterval({
      staticPath: path,
      format: props.format,
      count: props.imagesCount,
      interval,
      activeSequence,
    })

    if (images.length > 0) {
      // here we concat activateSequence
      activeSequence.images = activeSequence.images.concat(images)
      activeSequence.images.sort((a, b) => {
        const matchA = a.src.match(/(\d+)\.\w+$/)
        const matchB = b.src.match(/(\d+)\.\w+$/)
        if (matchA && matchB) {
          const indexA = parseInt(matchA[1], 10)
          const indexB = parseInt(matchB[1], 10)

          return indexA - indexB
        }

        return 0
      })
      activeSequence.alreadyLoaded = alreadyLoaded
    }

    if (callback) callback()
  } catch (err) {
    const error = err as Error
    logger.error(error.message, { error, owners: ['bot-squad-content-front'] })
  }
}

/**
 * Clears of the images of the currently selected category
 * TODO: improve this logic (preserve images of other categories)
 */
function clearImages() {
  imagesSequences.forEach((sequence) => {
    // eslint-disable-next-line no-param-reassign
    sequence.images = []
    sequence.alreadyLoaded.clear()
  })
}

/**
 *
 * Events
 */
function onResize(): void {
  devicePixelR = window.devicePixelRatio

  if (canvas.value) {
    const canvasBounds = canvas.value.getBoundingClientRect()

    updateCanvasPosition()

    canvasSizes.width = canvasBounds.width * devicePixelR
    canvasSizes.height = canvasBounds.height * devicePixelR
    canvas.value.width = canvasSizes.width
    canvas.value.height = canvasSizes.height
  }

  if (ctx.value) {
    ctx.value.scale(devicePixelR, devicePixelR)
  }
}

/**
 *
 * Hooks
 */

onMounted(async () => {
  if (!canvas.value) return
  ctx.value = canvas.value.getContext('2d')

  onResize()
})

defineExpose({
  addZoomEffect,
  onResize,
  loadImagesAtInterval,
  updateSequence,
  clearImages,
})
</script>
